import { Modal } from 'react-bootstrap';
import { useState, useEffect, useMemo } from "react";
import { notNullOrEmpty } from "../../utils/validators";
import { FloatingTextInput } from "../reusable-elements/floating-text-input";
import Comparer from "../../app/comparer";
import { DCSelector } from './dc-selector';
import ObjectID from 'bson-objectid';
import NumberHelpers from './../../app/number-helpers';

export const DebitCreditModal = ({ show, handleCancel, handleSave, selectedDC }) => {

    const comparer = new Comparer();
    const numberHelpers = new NumberHelpers();

    const defaultDC = useMemo(() => ({ id: new ObjectID().toHexString(), type: 'debit', reason: '', value: 0, date: new Date(new Date().setUTCHours(0, 0, 0, 0)) }), []);

    const [newDC, setNewDC] = useState(structuredClone(selectedDC ?? defaultDC));

    useEffect(() => {
        setNewDC(selectedDC ?? defaultDC);
    }, [selectedDC, defaultDC]);

    const handleStateUpdates = updateMethod => {
        setNewDC(current => {
            const draft = structuredClone(current);
            return updateMethod(draft);
        });
    };
    const hasChanges = () => {
        const equal = comparer.equals(newDC, selectedDC ?? defaultDC);
        return !equal;
    };

    const canSave = () => {
        if (!hasChanges()) return false;

        const parsed = numberHelpers.safeParseFloat(newDC.value, 2);
        const numericAmount = typeof parsed === 'number' ? parsed : parseFloat(parsed);

        const typeValid = newDC.type === 'debit' || newDC.type === 'credit';
        const reasonValid = notNullOrEmpty(newDC.reason);
        const dateValid = !!newDC.date;
        const amountValid = !!newDC.value && isFinite(numericAmount) && numericAmount !== 0;

        return typeValid && reasonValid && dateValid && amountValid;
    };

    const localHandleCancel = () => {
        setNewDC(structuredClone(defaultDC));
        handleCancel();
    }

    const localHandleSave = () => {
        handleSave(newDC);
        //setNewDC(null);
    }

    const formatDateForInput = (d) => {
        try {
            const iso = new Date(d).toISOString();
            return iso.split('T')[0];
        } catch {
            return new Date().toISOString().split('T')[0];
        }
    };

    return (
        <Modal
            show={show}
            onHide={localHandleCancel}
            backdrop="static"
            keyboard={true}
        >
            <Modal.Header closeButton>
                <Modal.Title>{`${!!selectedDC ? "Edit" : "Add"} Credit/Debit`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <DCSelector
                    onTypeSelected={newValue => { handleStateUpdates(v => { v.type = newValue; return v; }) }}
                    selectedType={newDC.type}
                />
                <FloatingTextInput
                    className={"mt-1"}
                    label={`${!newDC.reason ? "Enter" : ""} Reason`}
                    text={newDC.reason}
                    updateCallback={newValue => { handleStateUpdates(v => { v.reason = newValue; return v; }) }}
                    validatorFn={() => { return notNullOrEmpty(newDC.reason) }}
                    readOnly={false}
                />
                <FloatingTextInput
                    className={"mt-1"}
                    label={`${!newDC.value ? "Enter" : ""} Amount`}
                    text={newDC.value}
                    updateCallback={newValue => {
                        const val = newValue;
                        // Allow optional leading minus, digits, one optional decimal point (to support in-progress decimals like "50.")
                        if (/^-?\d*\.?\d*$/.test(val)) {
                            handleStateUpdates(v => { v.value = val; if (val && val.trim().startsWith('-')) { v.type = 'debit'; } return v; });
                        }
                    }}
                    onBlur={() => {
                        const parsed = numberHelpers.safeParseFloat(newDC.value, 2);
                        const numericAmount = typeof parsed === 'number' ? parsed : parseFloat(parsed);
                        if (!isFinite(numericAmount)) {
                            return;
                        }
                        setNewDC(current => {
                            const draft = structuredClone(current);
                            draft.value = numericAmount.toFixed(2);
                            return draft;
                        });
                    }}
                    validatorFn={() => { return notNullOrEmpty(newDC.value) }}
                    readOnly={false}
                />
                <div className="mt-3">
                    <label className="form-label">Date</label>
                    <input
                        type="date"
                        className="form-control"
                        value={formatDateForInput(newDC.date)}
                        onChange={(e) => {
                            const value = e.target.value; // YYYY-MM-DD
                            if (value) {
                                handleStateUpdates(v => { v.date = new Date(`${value}T00:00:00Z`); return v; });
                            }
                        }}
                        required
                    />
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className='btn-group'>
                    <button disabled={!canSave()} className='btn btn-primary' onClick={localHandleSave}>
                        Save
                    </button>
                    <button className='btn btn-secondary' onClick={localHandleCancel}>
                        Close
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );

};